/**
 * The campaigns the period holds, each with the instant that put it there.
 *
 * **The send instant is a column, not a detail.** A period is a boundary
 * argument, and the only way a reader can check a boundary is to see which side
 * of it each campaign fell on — so `sentAt` is printed in the *period's* zone,
 * with the zone named, next to the campaign it dated. A campaign sent at 23:50
 * on the last day is in this period entirely; one sent ten minutes later is in
 * the next one entirely; and both facts are legible from this table rather than
 * taken on trust. Rendering the instant in the reader's own zone — which is what
 * `formatDateTime` would do — is what makes a September campaign look like an
 * October one to somebody reading from another continent, on a screen whose
 * whole subject is which period something belongs to.
 *
 * **`campaignCount` is not `campaigns.length`, and this file never substitutes
 * one for the other.** The count is what the period holds; the list is the page
 * being looked at, and the API may send fewer rows than the period contains. A
 * screen deriving "23 campaigns" from the array it happens to have says 25 when
 * the period holds 400 — so the count is printed from `campaignCount`, and when
 * the list is shorter than the count the table says so instead of quietly
 * showing a truncated period as a complete one.
 *
 * **Reach on a row is that campaign's distinct addresses**, in the channel's own
 * column, and the header says `distinct addresses` in words. It is not a row
 * count and it is not comparable across rows by addition: two campaigns to the
 * same list have the same reach and did not reach twice as many people. The
 * period's own headline — distinct recipients — is the figure that answers that
 * question, and it is computed once over all of them rather than summed here.
 *
 * **No row shows a failure figure**, and that is the discriminated union doing
 * its job rather than a layout choice: `analytics.notDelivered` on the
 * un-narrowed `ChannelAnalytics` does not compile, because the email branch's
 * five SES outcomes and the WhatsApp branch's seven Meta classes are unrelated
 * types. A column that showed both would have to invent a word covering a hard
 * bounce and a pair rate limit, and there is no such word. The per-channel
 * sections above the table are where failures live, in their own vocabularies.
 *
 * **An empty period is a sentence.** `NO_CAMPAIGNS_IN_RANGE_NOTE`, from the
 * contract, drawing the distinction a grid of zeros destroys: nothing went out,
 * which is not the same as something going out and reaching nobody.
 *
 * **Slice 17 shortened both paragraphs and kept every claim in them.** The
 * count still comes from `campaignCount` and still says so when the list is
 * shorter than the period; the zone is still named beside the sent column; the
 * reach column still says these figures do not add up, and still points at the
 * distinct-recipient figure as the one that answers the question they cannot.
 * What went was the sentence explaining that this is so a campaign near a
 * boundary can be checked — the check is what the column is *for*, and a reader
 * looking at a timestamp in a named zone does not need to be told they may look
 * at it.
 */
import {
  REACH_UNIT_LABEL,
  formatInstantInZone,
  type AnalyticsRange,
  type CampaignAnalytics,
  type RowCount,
} from '@wend/shared';
import { DataTable, type DataTableColumn } from '@wend/ui';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { formatCount, pluralise } from '../../lib/format';
import { channelSurfaceLabel } from '../../lib/surface-display';
import { NoCampaignsNote } from './AnalyticsCopy';

export interface AnalyticsCampaignsProps {
  campaigns: readonly CampaignAnalytics[];
  /** What the period holds. May exceed the list — see the header. */
  campaignCount: RowCount;
  /** The period, for the zone every instant below is printed in. */
  range: AnalyticsRange;
}

export function AnalyticsCampaigns({ campaigns, campaignCount, range }: AnalyticsCampaignsProps) {
  const columns = useMemo<Array<DataTableColumn<CampaignAnalytics>>>(
    () => [
      {
        key: 'name',
        header: 'Campaign',
        cell: (campaign) => (
          <Link
            to={`/campaigns/${encodeURIComponent(campaign.campaignId)}`}
            className="text-text underline decoration-line underline-offset-4 hover:decoration-line-strong"
          >
            {campaign.name}
          </Link>
        ),
      },
      {
        /* Slice 44: the surface, by the taxonomy's label — the dimension
           every figure is attributed to, rather than the provider's channel. */
        key: 'surface',
        header: 'Surface',
        width: '9rem',
        cell: (campaign) => channelSurfaceLabel(campaign.analytics.channel),
      },
      {
        key: 'sentAt',
        header: `Sent (${range.timezone})`,
        width: '16rem',
        cell: (campaign) => (
          <span className="tabular-nums">
            {formatInstantInZone(new Date(campaign.sentAt), range.timezone)}
          </span>
        ),
      },
      {
        key: 'reach',
        header: REACH_UNIT_LABEL,
        width: '12rem',
        align: 'right',
        cell: (campaign) => (
          <span className="tabular-nums">{formatCount(campaign.analytics.reach.addresses)}</span>
        ),
      },
    ],
    [range.timezone],
  );

  const truncated = campaigns.length < campaignCount.rows;

  return (
    <div className="flex flex-col gap-4">
      {campaignCount.rows === 0 ? (
        <NoCampaignsNote />
      ) : (
        <>
          <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
            {pluralise(campaignCount.rows, 'campaign')} went out in this period, newest first, each
            dated by the instant it was sent and printed in{' '}
            <span className="text-text">{range.timezone}</span> — the zone the period&rsquo;s
            boundaries were read in.{' '}
            {truncated
              ? `${
                  campaigns.length === 1
                    ? 'The most recent one is listed'
                    : `The ${formatCount(campaigns.length)} most recent are listed`
                }; the figures above cover all ${formatCount(campaignCount.rows)}.`
              : 'All of them are listed.'}
          </p>

          <DataTable
            label="Campaigns in this period"
            columns={columns}
            rows={campaigns}
            getRowKey={(campaign) => campaign.campaignId}
            minWidth="52rem"
            empty={
              <p className="max-w-prose text-[0.9rem] text-muted">
                The period holds {pluralise(campaignCount.rows, 'campaign')} and none of them came
                back with this list. Nothing has been counted as zero — reload before reading
                anything into it.
              </p>
            }
          />

          <p className="max-w-prose text-[0.82rem] leading-relaxed text-muted">
            The reach column counts {REACH_UNIT_LABEL} per campaign and these do not add up: two
            campaigns to the same list have the same reach and did not reach twice as many people.
            The distinct-recipient figure at the top of the screen answers that.
          </p>
        </>
      )}
    </div>
  );
}
